import { Spinner } from '../components/feedback';
import { OperationBoundary } from '../components/OperationBoundary';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { advancedGameShape, GameSettingsPatchSchema, gameSettingsPatch, type GameSettings } from '@mcpanel/core';
import { ServerPropertiesFields } from '../components/ServerPropertiesFields';
import { api } from '../api/client';
import type { ChangeRequest, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { GameSettingsFields } from '../components/GameSettingsFields';
import { ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { PendingForServer, ReviewDialog } from './ServerManage';

const fieldLabels: Record<string, string> = {
  'max-players': '最大プレイヤー数',
  'spawn-protection': 'スポーン保護範囲',
  motd: 'サーバーの説明（MOTD）',
};
const fieldLabel = (key: string) =>
  fieldLabels[key] ?? (key in advancedGameShape ? `${key}（サーバープロパティの詳細）` : key);

function Editor({ server, current }: { server: ServerView; current: GameSettings }) {
  const { canOperate, notify } = useApp();
  const [params] = useSearchParams();
  const focus = params.get('focus');
  const [advanced, setAdvanced] = useState(() => focus?.startsWith('property-') ?? false);
  useEffect(() => {
    if (focus) setAdvanced(focus.startsWith('property-'));
  }, [focus]);
  const [value, setValue] = useState(current);
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);
  const patch = gameSettingsPatch(value, current);
  const checked = GameSettingsPatchSchema.safeParse(patch);
  // 直す項目が「サーバープロパティの詳細」に隠れていても分かるよう、ボタンの横に名前を出す
  const invalid =
    Object.keys(patch).length > 0 && !checked.success
      ? [...new Set(checked.error.issues.map((issue) => String(issue.path[0] ?? '')))].filter(Boolean)
      : [];
  useEditDirty(Object.keys(patch).length > 0);
  async function plan() {
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_game_settings', GameSettingsPatchSchema.parse(patch), server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card card-body stack">
      <h2>ゲーム設定</h2>
      <p>現在のゲーム設定です。ファイル設定の変更は再起動し、ゲームルールのみの変更は起動中ならすぐに反映します。</p>
      <button className="btn" onClick={() => setAdvanced(!advanced)}>
        {advanced ? 'ゲーム設定に戻る' : 'サーバープロパティの詳細'}
      </button>
      {advanced ? (
        <ServerPropertiesFields value={value} onChange={setValue} disabled={!canOperate || busy} />
      ) : (
        <>
          <GameSettingsFields value={value} onChange={setValue} disabled={!canOperate || busy} />
          {value['allow-end'] !== undefined ? (
            <label className="check" data-search-id="game-allow-end">
              <input
                type="checkbox"
                checked={value['allow-end']}
                disabled={!canOperate || busy}
                onChange={(e) => setValue({ ...value, 'allow-end': e.target.checked })}
              />
              エンドを使う
            </label>
          ) : (
            <p>このソフトではエンドを止める設定がありません。</p>
          )}
        </>
      )}
      <p>ネザー・エンドを無効にしてもワールドデータは消しません。版に応じてゲームルールと設定ファイルへ反映します。</p>
      {canOperate && (
        <button
          className="btn primary"
          disabled={busy || Object.keys(patch).length === 0 || !checked.success}
          onClick={() => void plan()}
        >
          {busy && <Spinner />}
          変更内容を確認
        </button>
      )}
      {invalid.length > 0 && (
        <p role="alert" className="small">
          次の項目を直すと確認できます: {invalid.map(fieldLabel).join('、')}
        </p>
      )}
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </section>
  );
}
function GameSettingsTabContent({ server }: { server: ServerView }) {
  const result = usePoll(() => api.gameSettings(server.id), 10000, [server.id, server.revision]);
  return (
    <div className="stack">
      <PendingForServer serverId={server.id} />
      <ErrorAlert error={result.error} />
      {result.data && <Editor server={server} current={result.data} />}
    </div>
  );
}

export function GameSettingsTab({ server }: { server: ServerView }) {
  return (
    <OperationBoundary serverId={server.id}>
      <EditBoundary
        resource={{ target: `server:${server.id}`, screen: 'game-settings' }}
        revision={String(server.revision)}
      >
        <GameSettingsTabContent server={server} />
      </EditBoundary>
    </OperationBoundary>
  );
}
